<route lang="json">
{
  "name": "classroom_screen-public-show",
  "meta": {
    "namespace": "classroom_screen"
  }
}
</route>
<script setup lang="ts">
import { useTeachingBoardScreenQueries } from "@/composables/classroomScreen/useTeachingBoardScreenQueries";
import {
  type ClassroomScreenScreen,
  type ClassroomScreenPage,
} from "@/services/fobizzToolsApi";
import { type User } from "@/services/orval/internalApiV1";
import { useClassroomScreenStore } from "@/stores/classroomScreen";
import { subscribeToBoardChannel } from "@/subscribers/classroomScreen";

const route = useRoute("classroom_screen-public-show");
const router = useRouter();
const screenStore = useClassroomScreenStore();
const userStore = useUserStore();

const currentPage = ref<ClassroomScreenPage>();
const screenId = computed(() => route.params.id);
const { getQuery } = useTeachingBoardScreenQueries();

const { data: screen, ...asyncStatus } = getQuery(screenId);

const initScreenStore = (data: ClassroomScreenScreen) => {
  screenStore.$patch({ screen: data });
  currentPage.value = screenStore.firstPage;

  isParentInitialised.value = true;
};

const pageShowRouteName = computed(() =>
  route.name === "classroom_screen-public-show"
    ? "classroom_screen-public-page-show"
    : "classroom_screen-page-show"
);
const handleRedirectToPage = async () => {
  if (!currentPage.value) return;

  await router.isReady();

  if (
    router.currentRoute.value.name === "classroom_screen-show" ||
    router.currentRoute.value.name === "classroom_screen-public-show"
  ) {
    // redirect to first page
    router.replace({
      name: pageShowRouteName.value,
      params: {
        id: route.params.id,
        pageId: currentPage.value.id,
      },
      query: route.query,
    });
  }
};
const subscriber = subscribeToBoardChannel(
  screenId.value,
  (userStore.user as User)?.toolsUserId
);

onBeforeUnmount(() => {
  if (subscriber) {
    subscriber.unsubscribe();
  }
});

const isParentInitialised = ref(false);

const parentW = ref(0);
const parentH = ref(0);

const calculateParentDimensions = () => {
  parentW.value = document.body.clientWidth;
  parentH.value = document.body.clientHeight;
};

onMounted(() => {
  calculateParentDimensions();
  window.addEventListener("resize", calculateParentDimensions);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", calculateParentDimensions);
});

const setupScreen = () => {
  if (!screen.value) return;

  handleRedirectToPage();
};

watch(
  () => screen.value,
  (newScreen, oldScreen) => {
    !!newScreen && initScreenStore(JSON.parse(JSON.stringify(newScreen)));
    if (newScreen?.id !== oldScreen?.id) {
      setupScreen();
    }
  },
  { immediate: true }
);

const { locale } = useI18n();
</script>

<template>
  <WithVisitor>
    <AsyncLoader :async-status="asyncStatus">
      <template v-if="screen" #resolved>
        <div :key="`${locale}-${route.path}`">
          <RouterView v-if="isParentInitialised" :screen="screen" />
        </div>
      </template>
    </AsyncLoader>
  </WithVisitor>
</template>
